import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { baseUrl, getApiUrl } from '../shared';
import { useUser, useAuth } from '@clerk/clerk-react';

const UserContext = createContext();

export const UserProvider = ({ children }) => {
    const { user: clerkUser, isLoaded: isClerkLoaded } = useUser();
    const { getToken, signOut } = useAuth();

    // Read token ONCE at module init time (not during render) to avoid re-render loops
    const initialToken = useRef(localStorage.getItem('access'));
    const hasToken = !!initialToken.current;

    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(false);
    // If token exists, assume logged-in immediately so PrivateRoute doesn't show Loader
    const [loggedIn, setLoggedIn] = useState(hasToken);
    // If token exists, skip the appLoading phase entirely
    const [appLoading, setAppLoading] = useState(!hasToken);
    const [friendRequests, setFriendRequests] = useState([]);
    const [forceFetchFriendRequests, setForceFetchFriendRequests] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [hasUnreadMessages, setHasUnreadMessages] = useState(false);

    // Keep getToken stable via ref to prevent infinite re-auth loops
    const getTokenRef = useRef(getToken);
    useEffect(() => {
        getTokenRef.current = getToken;
    }, [getToken]);

    // If we already have a token, pre-load the user profile in the background
    // so the page has data immediately on refresh.
    useEffect(() => {
        const token = initialToken.current;
        if (token) {
            axios.get(getApiUrl('api/user/'), {
                headers: { Authorization: `Bearer ${token}` },
            }).then(res => {
                setUser(res.data);
            }).catch(() => {
                // Token expired — clean up, Clerk will re-sync
                localStorage.removeItem('access');
                localStorage.removeItem('refresh');
                setLoggedIn(false);
                setAppLoading(false);
            });
        }
        // Run only on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Safety timeout to prevent hanging on localhost if Clerk fails/is blocked
    useEffect(() => {
        const timer = setTimeout(() => {
            if (!isClerkLoaded) {
                console.warn("Clerk loading timed out. Setting appLoading to false to unblock rendering.");
                setAppLoading(false);
            }
        }, 3000);
        return () => clearTimeout(timer);
    }, [isClerkLoaded]);


    // Sync Clerk Session with Backend
    // Only re-runs when the actual Clerk user identity changes (not on every render)
    useEffect(() => {
        const syncUser = async () => {
            if (!isClerkLoaded) return;

            if (clerkUser) {
                setLoading(true);
                try {
                    const token = await getTokenRef.current();
                    const response = await axios.post(getApiUrl('api/login/clerk/'), { token });

                    if (response.data.access) {
                        localStorage.setItem('access', response.data.access);
                        localStorage.setItem('refresh', response.data.refresh);
                        setLoggedIn(true);

                        const userResponse = await axios.get(getApiUrl('api/user/'), {
                            headers: { Authorization: `Bearer ${response.data.access}` },
                        });
                        setUser(userResponse.data);
                    }
                } catch (error) {
                    console.error("Error syncing with backend:", error);
                    handleLogout();
                } finally {
                    setLoading(false);
                    setAppLoading(false);
                }
            } else {
                // User is signed out in Clerk
                setUser(null);
                setLoggedIn(false);
                setAppLoading(false);
                localStorage.removeItem('access');
                localStorage.removeItem('refresh');
            }
        };

        syncUser();
        // Only re-run when Clerk user identity or loaded state changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clerkUser?.id, isClerkLoaded]);

    // Cross-tab Synchronization: Keep session in sync across multiple tabs
    useEffect(() => {
        const handleFocusAndVisibility = () => {
            const currentToken = localStorage.getItem('access');
            const isNowLoggedIn = !!currentToken;

            // If local storage says we are logged in, but React state says we are not,
            // OR local storage says we are logged out, but React state says we are in:
            // The tabs have fallen out of sync!
            if (loggedIn !== isNowLoggedIn) {
                console.log("Session state mismatch detected on tab focus. Syncing tab state...");
                // window.location.reload(); // Temporarily disabled to debug comment click reload issue
            }
        };

        const handleStorageChange = (e) => {
            // Storage events fire when another tab modifies localStorage.
            if (e.key === 'access' || e.key === 'refresh' || e.key === null) {
                handleFocusAndVisibility();
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                handleFocusAndVisibility();
            }
        };

        // Listen for both window focus and visibility changes to be completely certain we catch the user returning
        window.addEventListener('focus', handleFocusAndVisibility);
        window.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('focus', handleFocusAndVisibility);
            window.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [loggedIn]);

    const handleLogout = useCallback(async () => {
        await signOut();
        const hasSeenOnboarding = localStorage.getItem('hasSeenOnboarding');
        localStorage.clear();
        if (hasSeenOnboarding) {
            localStorage.setItem('hasSeenOnboarding', hasSeenOnboarding);
        }
        setUser(null);
        setLoggedIn(false);
        setAppLoading(false);
        setFriendRequests([]);
    }, [signOut]);

    const updateLoggedInStatus = async (isLoggedIn) => {
        if (!isLoggedIn) handleLogout();
    };

    const fetchUser = useCallback(async () => {
        const token = localStorage.getItem('access');
        if (token) {
            const response = await axios.get(getApiUrl('api/user/'), {
                headers: { Authorization: `Bearer ${token}` },
            });
            setUser(response.data);
        }
    }, []);

    const fetchFriendRequests = useCallback(async () => {
        const token = localStorage.getItem('access');
        if (!token) return;
        try {
            const response = await axios.get(getApiUrl('api/friend-requests/'), {
                headers: { Authorization: `Bearer ${token}` },
            });
            setFriendRequests(response.data);
        } catch (error) {
            console.error("Error fetching friend requests:", error);
        }
    }, []);

    const fetchNotifications = useCallback(async () => {
        const token = localStorage.getItem('access');
        if (!token) return;
        try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const [notifsRes, countRes] = await Promise.all([
                axios.get(getApiUrl('api/notifications/'), config),
                axios.get(getApiUrl('api/notifications/count/'), config)
            ]);

            setNotifications(notifsRes.data);
            setUnreadCount(countRes.data.unread_count);
            setHasUnreadMessages(countRes.data.has_unread_messages);
        } catch (error) {
            console.error('Error fetching notifications:', error);
        }
    }, []);

    // Fetch friend requests once when first logged in
    useEffect(() => {
        if (loggedIn) {
            fetchFriendRequests();
            fetchNotifications();
            const interval = setInterval(fetchNotifications, 15000);
            return () => clearInterval(interval);
        } else {
            setNotifications([]);
            setUnreadCount(0);
        }
    }, [loggedIn, fetchFriendRequests, fetchNotifications]);

    // Fetch when forced (e.g., after accepting a request or marking read)
    useEffect(() => {
        if (forceFetchFriendRequests) {
            fetchFriendRequests();
            fetchNotifications();
            setForceFetchFriendRequests(false);
        }
    }, [forceFetchFriendRequests, fetchFriendRequests, fetchNotifications]);

    const value = {
        user,
        loading,
        loggedIn,
        fetchUser,
        handleLogout,
        updateLoggedInStatus,
        appLoading,
        friendRequests,
        notifications,
        unreadCount,
        hasUnreadMessages,
        setNotifications,
        setUnreadCount,
        setHasUnreadMessages,
        fetchNotifications,
        setForceFetchFriendRequests
    };

    return (
        <UserContext.Provider value={value}>
            {children}
        </UserContext.Provider>
    );
};

export const useCurrentUser = () => useContext(UserContext);